import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useUiLocale } from '../../context/UiLocaleContext';

const AnimatePresenceSafe = AnimatePresence as React.FC<{ children: React.ReactNode }>;

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title: string;
  message: string;
  type?: 'info' | 'success' | 'warning' | 'error' | 'confirm';
  confirmText?: string;
  cancelText?: string;
  icon?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'info',
  confirmText = 'OK',
  cancelText = 'Cancel',
  icon
}) => {
  const { uiLocale } = useUiLocale();
  const titleId = `modal-title-${useId().replace(/:/g, '')}`;
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  /** RTL: primary action should appear on the reading-start (visual right); keep footer justify-end. */
  const confirmActionsRtl = uiLocale === 'he' && Boolean(onConfirm);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    setReduceMotion(
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const focusFirst = () => {
      const panel = panelRef.current;
      const focusables = panel
        ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        : [];
      focusables[0]?.focus();
    };
    focusFirst();
    requestAnimationFrame(focusFirst);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const panel = panelRef.current;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusables.length === 0) return;
      const firstEl = focusables[0];
      const lastEl = focusables[focusables.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else if (document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) return;
    const el = previousFocusRef.current;
    previousFocusRef.current = null;
    if (el && typeof el.focus === 'function') {
      window.setTimeout(() => el.focus(), 0);
    }
  }, [isOpen]);

  const getModalColors = () => {
    switch (type) {
      case 'success':
        return {
          titleColor: 'text-green-900',
          messageColor: 'text-green-800',
          iconBg: 'bg-green-500/20 backdrop-blur-sm border border-green-300/30',
          iconColor: 'text-green-700',
          confirmBg: 'bg-green-600/90 hover:bg-green-700/90 backdrop-blur-sm',
          defaultIcon: '✅'
        };
      case 'warning':
        return {
          titleColor: 'text-yellow-900',
          messageColor: 'text-yellow-800',
          iconBg: 'bg-yellow-500/20 backdrop-blur-sm border border-yellow-300/30',
          iconColor: 'text-yellow-700',
          confirmBg: 'bg-yellow-600/90 hover:bg-yellow-700/90 backdrop-blur-sm',
          defaultIcon: '⚠️'
        };
      case 'error':
        return {
          titleColor: 'text-red-900',
          messageColor: 'text-red-800',
          iconBg: 'bg-red-500/20 backdrop-blur-sm border border-red-300/30',
          iconColor: 'text-red-700',
          confirmBg: 'bg-red-600/90 hover:bg-red-700/90 backdrop-blur-sm',
          defaultIcon: '❌'
        };
      case 'confirm':
        return {
          titleColor: 'text-blue-900',
          messageColor: 'text-blue-800',
          iconBg: 'bg-blue-500/20 backdrop-blur-sm border border-blue-300/30',
          iconColor: 'text-blue-700',
          confirmBg: 'bg-blue-600/90 hover:bg-blue-700/90 backdrop-blur-sm',
          defaultIcon: '🔄'
        };
      default: // info
        return {
          titleColor: 'text-gray-900',
          messageColor: 'text-gray-800',
          iconBg: 'bg-orange-500/20 backdrop-blur-sm border border-orange-300/30',
          iconColor: 'text-orange-700',
          confirmBg: 'bg-orange-600/90 hover:bg-orange-700/90 backdrop-blur-sm',
          defaultIcon: 'ℹ️'
        };
    }
  };

  const colors = getModalColors();
  const displayIcon = icon || colors.defaultIcon;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const backdropTransition = reduceMotion ? { duration: 0 } : { duration: 0.2 };
  const panelTransition = reduceMotion ? { duration: 0 } : { duration: 0.25, ease: 'easeOut' as const };
  const backdropVariants = {
    initial: { opacity: reduceMotion ? 1 : 0 },
    animate: { opacity: 1 },
    exit: { opacity: reduceMotion ? 1 : 0 },
  };
  const panelVariants = {
    initial: { opacity: reduceMotion ? 1 : 0, scale: reduceMotion ? 1 : 0.98 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: reduceMotion ? 1 : 0, scale: reduceMotion ? 1 : 0.98 },
  };

  const modalBody = (
    <AnimatePresenceSafe>
      {isOpen && (
        <motion.div
          key="modal-backdrop"
          initial="initial"
          animate="animate"
          exit="exit"
          variants={backdropVariants}
          transition={backdropTransition}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={handleBackdropClick}
          role="presentation"
        >
          <motion.div
            ref={panelRef}
            key="modal-panel"
            variants={panelVariants}
            transition={panelTransition}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="relative rounded-2xl shadow-card max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Background Image Layer */}
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: "url('https://pub-dd11db53b680447d86c08d43682efe32.r2.dev/images/wizard/3_background.png')"
              }}
            />
            {/* Overlay for better readability */}
            <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px]" />
            {/* Content */}
            <div className="relative z-10 bg-gradient-to-br from-white/80 to-white/60 backdrop-blur-sm border border-white/30">
              <div className="p-6 pb-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`${colors.iconBg} rounded-full w-12 h-12 flex items-center justify-center text-xl shadow-soft`}>
                    <span className={colors.iconColor} aria-hidden>
                      {displayIcon}
                    </span>
                  </div>
                  <h2 id={titleId} className={`text-xl font-bold ${colors.titleColor} drop-shadow-sm`}>
                    {title}
                  </h2>
                </div>
                <p className={`${colors.messageColor} leading-relaxed font-bold drop-shadow-sm`}>
                  {message}
                </p>
              </div>
              <div className="px-6 pb-6 flex gap-3 justify-end">
                {confirmActionsRtl ? (
                  <>
                    <button
                      type="button"
                      onClick={onConfirm}
                      className={`${colors.confirmBg} text-white px-6 py-2.5 rounded-2xl font-semibold transition-all shadow-soft hover:shadow-card border border-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandOrange`}
                    >
                      {confirmText}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-5 py-2.5 text-body hover:text-heading font-semibold transition-colors bg-white/70 hover:bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-300/50 shadow-soft focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
                    >
                      {cancelText}
                    </button>
                  </>
                ) : (
                  <>
                    {onConfirm && (
                      <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 text-body hover:text-heading font-semibold transition-colors bg-white/70 hover:bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-300/50 shadow-soft focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
                      >
                        {cancelText}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={onConfirm || onClose}
                      className={`${colors.confirmBg} text-white px-6 py-2.5 rounded-2xl font-semibold transition-all shadow-soft hover:shadow-card border border-white/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brandOrange`}
                    >
                      {confirmText}
                    </button>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresenceSafe>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modalBody, document.body);
};

export default Modal;
